import { api } from './http';

export interface BillingEntry {
  id: number;
  matter_id: number;
  matter_title: string | null;
  user_id: number;
  user_name: string | null;
  entry_type: string;
  billing_date: string | null;
  duration_minutes: number | null;
  description: string | null;
  rate_cents: number | null;
  amount_cents: number | null;
  expense_category: string | null;
  vendor: string | null;
  receipt_reference: string | null;
  phase_milestone: string | null;
  billable: boolean;
  is_pro_bono: boolean;
  is_attested: boolean;
  attested_initials: string | null;
  attested_at: string | null;
  attestation_note: string | null;
  source: string;
  exported_at: string | null;
  activity_category: string | null;
  approved_for_billing: boolean;
  approved_by_user_id: number | null;
  approved_at: string | null;
  created_at: string;
}

export interface AttestationPayload {
  initials: string;
  note?: string;
}

export interface BillingEntryCreatePayload {
  matter_id: number;
  entry_type: string;
  source?: 'manual' | 'timer';
  billing_date?: string;
  duration_minutes?: number;
  rate_cents?: number;
  description: string;
  billable: boolean;
  is_pro_bono: boolean;
  activity_category?: string;
  phase_milestone?: string;
  amount_cents?: number;
  expense_category?: string;
  vendor?: string;
  receipt_reference?: string;
  /** Phase 6: when set, the suggestion is marked accepted as part of the slip save. */
  accepted_suggestion_id?: number;
  attestation?: AttestationPayload;
}

export interface BillingAmendPayload {
  description?: string;
  duration_minutes?: number;
  amount_cents?: number;
  billable?: boolean;
  is_pro_bono?: boolean;
  attestation: AttestationPayload;
}

export interface BillingSummary {
  matters: {
    matter_id: number;
    matter_title: string | null;
    total_minutes: number;
    billable_minutes: number;
    expense_cents: number;
    pro_bono_minutes: number;
    entry_count: number;
  }[];
}

export const createBillingEntry = (data: BillingEntryCreatePayload) => {
  if (!Number.isInteger(data.matter_id) || data.matter_id <= 0) {
    return Promise.reject(new Error('Matter is required to save a billing slip.'));
  }
  return api.post<BillingEntry>('/billing/entries', data).then(r => r.data);
};

export const listBillingEntries = (params?: {
  start_date?: string;
  end_date?: string;
  matter_id?: number;
  user_id?: number;
  attested_only?: boolean;
  unexported_only?: boolean;
  pro_bono_only?: boolean;
}) =>
  api.get<BillingEntry[]>('/billing/entries', { params }).then(r => r.data);

export const attestBillingEntry = (entryId: number, data: AttestationPayload) =>
  api.post<BillingEntry>(`/billing/entries/${entryId}/attest`, data).then(r => r.data);

export const amendBillingEntry = (entryId: number, data: BillingAmendPayload) =>
  api.patch<BillingEntry>(`/billing/entries/${entryId}/amend`, data).then(r => r.data);

export const deleteBillingEntry = (entryId: number) =>
  api.delete(`/billing/entries/${entryId}`);

export interface BillingEntryUpdatePayload {
  description?: string;
  entry_type?: string;
  billing_date?: string;
  duration_minutes?: number;
  rate_cents?: number;
  amount_cents?: number;
  billable?: boolean;
  is_pro_bono?: boolean;
  activity_category?: string;
  phase_milestone?: string;
  expense_category?: string;
  vendor?: string;
  receipt_reference?: string;
}

export const updateBillingEntry = (entryId: number, data: BillingEntryUpdatePayload) =>
  api.patch<BillingEntry>(`/billing/entries/${entryId}`, data).then(r => r.data);

export const bulkApproveEntries = (entry_ids: number[], approved_for_billing = true) =>
  api.post<BillingEntry[]>('/billing/entries/bulk-approve', { entry_ids, approved_for_billing }).then(r => r.data);

export const getBillingSummary = (params?: { start_date?: string; end_date?: string }) =>
  api.get<BillingSummary>('/billing/summary', { params }).then(r => r.data);

// ── LeanLaw push ──────────────────────────────────────────
// POST entry_ids to the dedicated push endpoint; returns the PushResult
// shape the BillingExportPage's "Send to LeanLaw" button uses to toast
// per-entry success/failure counts. Backend gates on the `leanlaw`
// feature flag — frontend's useFeatureEnabled('leanlaw') controls the
// button's enabled/disabled state defensively in parallel.

export interface LeanLawPushResult {
  pushed: number;
  skipped_already_pushed: number;
  skipped_not_approved: number;
  failed: number;
  failure_messages: string[];
}

export const pushToLeanLaw = (entry_ids: number[]) =>
  api.post<LeanLawPushResult>('/billing/leanlaw/push', { entry_ids }).then(r => r.data);

// ── Phase 3: suggestions + activity log ───────────────────

export interface BillingSuggestion {
  id: number;
  user_id: number;
  matter_id: number;
  matter_title: string | null;
  target_date: string;
  entry_type: string;
  suggested_duration_minutes: number | null;
  suggested_amount_cents: number | null;
  suggested_narrative: string;
  ai_reasoning: string;
  supporting_event_ids: number[];
  status: 'pending' | 'accepted' | 'dismissed';
  dismiss_reason: string | null;
  created_at: string;
  actioned_at: string | null;
}

export interface ActivityLogEvent {
  id: number;
  user_id: number;
  matter_id: number | null;
  source: string;
  event_type: string;
  description: string | null;
  occurred_at: string;
  duration_seconds: number | null;
  created_at: string;
}

export const getSuggestions = (date: string) =>
  api.get<BillingSuggestion[]>(`/billing/suggestions/${date}`).then(r => r.data);

export const getSuggestionsRange = (start_date: string, end_date: string, user_id?: number) =>
  api.get<BillingSuggestion[]>('/billing/suggestions', { params: { start_date, end_date, user_id } }).then(r => r.data);

/** Phase 6: only dismiss is allowed; acceptance happens via the billing slip save. */
export const dismissSuggestion = (id: number, dismiss_reason?: string) =>
  api
    .patch<BillingSuggestion>(`/billing/suggestions/${id}`, { status: 'dismissed', dismiss_reason })
    .then(r => r.data);

export const getActivityLog = (params: { log_date?: string; start_date?: string; end_date?: string; matter_id?: number; user_id?: number }) =>
  api.get<ActivityLogEvent[]>('/billing/activity-log', { params }).then(r => r.data);

// ── Phase 4: rate resolution + admin profile CRUD ─────────

export interface ResolvedRate {
  rate_cents: number | null;
  source: 'matter_override' | 'practice_area_profile' | 'user_default' | 'none';
  practice_area_id: number | null;
  rounding_minutes: number;
}

export const getResolvedRate = (matterId?: number) =>
  api
    .get<ResolvedRate>('/billing/rate', { params: matterId ? { matter_id: matterId } : {} })
    .then(r => r.data);

export interface BillingProfile {
  id: number;
  user_id: number;
  practice_area_id: number;
  practice_area_name: string | null;
  rate_cents: number;
  effective_date: string;
  created_at: string;
  updated_at: string;
}

export const listUserBillingProfiles = (userId: number) =>
  api.get<BillingProfile[]>(`/billing/admin/users/${userId}/profiles`).then(r => r.data);

export const upsertUserBillingProfile = (
  userId: number,
  body: { practice_area_id: number; rate_cents: number; effective_date: string },
) => api.post<BillingProfile>(`/billing/admin/users/${userId}/profiles`, body).then(r => r.data);

export const deleteUserBillingProfile = (userId: number, profileId: number) =>
  api.delete<void>(`/billing/admin/users/${userId}/profiles/${profileId}`).then(r => r.data);

export const setUserDefaultRate = (userId: number, billableRateCents: number | null) =>
  api
    .patch<{ user_id: number; billable_rate_cents: number | null }>(
      `/billing/admin/users/${userId}/default-rate`,
      { billable_rate_cents: billableRateCents },
    )
    .then(r => r.data);

// ── Phase 5: manual review trigger ────────────────────────

export interface ReviewTriggerSummary {
  dates_processed: string[];
  dates_skipped: string[];
  users_processed: number;
  matters_analyzed: number;
  suggestions_created: number;
  api_calls_made: number;
  budget_remaining_cents: number | null;
}

export const triggerReview = (start_date: string, end_date: string) =>
  api.post<ReviewTriggerSummary>('/billing/review/trigger', { start_date, end_date }).then(r => r.data);

// ── Phase 6: approval + re-export + pending count ─────────

export const approveBillingEntry = (entryId: number, approved: boolean) =>
  api
    .patch<BillingEntry>(`/billing/entries/${entryId}/approve`, { approved_for_billing: approved })
    .then(r => r.data);

export const adminReExportEntry = (entryId: number, reason: string) =>
  api
    .post<BillingEntry>(`/billing/admin/entries/${entryId}/re-export`, { reason })
    .then(r => r.data);

export interface PendingSuggestionsCount {
  pending: number;
  latest_target_date: string | null;
}

export const getPendingSuggestionsCount = () =>
  api.get<PendingSuggestionsCount>('/billing/suggestions/pending-count').then(r => r.data);

// ── Phase 7: period-end report ────────────────────────────

export type ReportPeriod = 'weekly' | 'semi_monthly' | 'monthly' | 'custom';

export interface ReportParams {
  period: ReportPeriod;
  start?: string;
  end?: string;
  matter_id?: number;
  user_id?: number;
  approved_only?: boolean;
  unexported_only?: boolean;
  mark_as_exported?: boolean;
}

/** Returns the PDF as a Blob; caller is expected to trigger the browser download. */
export const generateBillingReport = (params: ReportParams) =>
  api
    .get('/billing/report', { params: { format: 'pdf', ...params }, responseType: 'blob' })
    .then(r => r.data as Blob);

export interface BillingCsvExportParams {
  start_date?: string;
  end_date?: string;
  matter_id?: number;
  approved_only?: boolean;
  unexported_only?: boolean;
  mark_as_exported?: boolean;
}

/** Returns the CSV as a Blob; caller is expected to trigger the browser download. */
export const exportBillingCsv = (params: BillingCsvExportParams) =>
  api
    .get('/billing/entries/export.csv', { params, responseType: 'blob' })
    .then(r => r.data as Blob);

// ── Phase 8: customer-facing AI cost ──────────────────────

export interface AiCostAgentLine {
  system: string;
  display_name: string;
  call_count: number;
  cost_cents_raw: number;
  cost_cents_with_markup: number;
  tokens_in: number;
  tokens_out: number;
}

export interface MonthlyAiCost {
  month: string;
  period_start: string;
  period_end: string;
  total_cost_cents_raw: number;
  total_cost_cents_with_markup: number;
  markup_multiplier: number;
  agents: AiCostAgentLine[];
}

export const getMonthlyAiCost = (month: string) =>
  api.get<MonthlyAiCost>('/billing/ai-cost', { params: { month } }).then(r => r.data);

// ── Admin AI billing (platform billing to customer firms) ───

export const getAdminAiBilling = (month: string) =>
  api.get<MonthlyAiCost>('/admin/ai-billing', { params: { month } }).then(r => r.data);

export const exportAdminAiBillingCsv = (month: string) => {
  const url = `/api/admin/ai-billing/export.csv?month=${month}`;
  window.open(url, '_blank');
};

export interface ZohoInvoiceResult {
  status: string;
  invoice_id?: string;
  invoice_number?: string;
  reference_number?: string;
  total_billed_cents?: number;
  line_items?: number;
  month?: string;
  message?: string;
}

export const pushAdminAiBillingToZoho = (month: string, customerId?: string, customerName?: string) =>
  api.post<ZohoInvoiceResult>('/admin/ai-billing/zoho/draft-invoice', {
    confirmed: true,
    customer_id: customerId,
    customer_name: customerName,
  }, {
    params: { month },
  }).then(r => r.data);

export interface BillingCustomer {
  customer_id: string;
  customer_name: string;
  is_default: boolean;
}

export const getAdminBillingCustomers = () =>
  api.get<BillingCustomer[]>('/admin/ai-billing/customers').then(r => r.data);

export interface ExportLogEntry {
  id: number;
  month: string;
  customer_id: string;
  customer_name: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  total_billed_cents: number;
  line_items: number;
  exported_at: string;
  exported_by: string | null;
}

export const getAdminExportHistory = (month?: string, customerId?: string) =>
  api.get<ExportLogEntry[]>('/admin/ai-billing/export-history', {
    params: { month, customer_id: customerId },
  }).then(r => r.data);

// ── Phase 9: billing integration registry ─────────────────

export interface BillingIntegrationFormat {
  slug: 'csv' | 'ledes_1998b' | 'clio' | 'timesolv' | 'tabs3' | 'bill4time';
  display_name: string;
  description: string;
  implemented: boolean;
  doc_url: string | null;
}

export const listBillingIntegrations = () =>
  api.get<BillingIntegrationFormat[]>('/billing/integrations').then(r => r.data);

// ── Firm billing settings (#1859) ─────────────────────────
// Served from /api/billing-settings, outside the billing_recovery-gated
// /api/billing router, so the manual time-entry preview keeps working when
// that flag is off.

export interface BillingSettings {
  time_rounding_minutes: number;
}

export const getBillingSettings = () =>
  api.get<BillingSettings>('/billing-settings').then(r => r.data);

export const updateBillingSettings = (timeRoundingMinutes: number) =>
  api
    .patch<BillingSettings>('/billing-settings', { time_rounding_minutes: timeRoundingMinutes })
    .then(r => r.data);
